import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';

import { GridRow, Typography, theme } from '@innovamat/glimmer-components';
import type { Applet } from '@innovamat/glow-api-client';
import { useUnblockedApplets } from '@innovamat/glow-api-client';
import { stages } from '@innovamat/radiance-utils';
import { StyledCol, StyledGridContainer } from '@innovamat/smart-guides';
import { useWebviewBridge } from '@innovamat/webview-bridge';
import { isJourneyClassroom as checkIsJourneyClassroom } from '../../../classrooms-management/modules/classroom';

import { useCurrentNavigationValues, useNavigation } from '../../../navigation';
import { useMarkSessionDone } from '../../../session-done';
import { useUser } from '../../../user-management';
import { useJourneyUnblockedApplets } from '../../hooks/use-journey-unblocked-applets';
import { useLocalizationParams } from '../../hooks/use-localization-params';
import { ContentList, ContentListSkeleton } from '../content-list';
import { useUnblockedAppletsValues } from './use-unblocked-applets-values';
import { createContentListByApplets } from './utils';

type UnblockedAppletsListProps = {
  appletsCode: string;
  status?: string;
};

const UnblockedAppletsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UnblockedAppletsHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 64px;
  margin-bottom: 12px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-bottom: 4px;
  }
`;

const StyledRowLoader = styled.div`
  margin-top: 64px;
`;

const INNOVAMAT_BLOG_URL =
  'https://blog.innovamat.com/practica-digital-matematiques-app-innovamat/';

export function UnblockedAppletsList({
  appletsCode,
  status,
}: UnblockedAppletsListProps): JSX.Element | null {
  const { t } = useTranslation();
  const { user, isRole } = useUser();
  const { isInWebview } = useWebviewBridge();
  const { onMarkSession, onToggleSolutions } = useMarkSessionDone();
  const { goToResource } = useNavigation();
  const { language, region } = useLocalizationParams();
  const { currentClassroom } = useCurrentNavigationValues();

  const {
    classroomId,
    courseOrder,
    courseId,
    isStudentWithoutCourse,
    isTeacherWithoutCourseOrApplets,
  } = useUnblockedAppletsValues();

  const isEarlyYears = stages.isInfantil(courseOrder);
  const isPrimaria = stages.isPrimaria(courseOrder);

  const isSessionDone = status === 'done';
  const isJourneyClassroom = checkIsJourneyClassroom(currentClassroom);

  const {
    applets: journeyUnblockedApplets,
    isPending: isJourneyUnblockedAppletsPending,
  } = useJourneyUnblockedApplets({
    sessionCode: appletsCode,
    language,
    region,
    enabled: isJourneyClassroom,
  });

  const { data, isPending: isUnblockedAppletsPending } = useUnblockedApplets({
    language: language,
    courseId,
    courseOrder,
    sessionCode: appletsCode,
    region: region,
    enabled: !isJourneyClassroom,
  });

  const isPending = isJourneyClassroom
    ? isJourneyUnblockedAppletsPending
    : isUnblockedAppletsPending;

  if (isStudentWithoutCourse) {
    return null;
  }

  if (!isJourneyClassroom && isTeacherWithoutCourseOrApplets(appletsCode)) {
    return null;
  }

  if (isPending)
    return (
      <StyledGridContainer>
        <StyledRowLoader>
          <ContentListSkeleton />
        </StyledRowLoader>
      </StyledGridContainer>
    );
  const applets = isJourneyClassroom
    ? (journeyUnblockedApplets as Applet[])
    : (data?.appletsBySessionCode as Applet[]);

  if (!applets || applets?.length === 0) {
    return null;
  }

  const appletsList = createContentListByApplets(applets, isSessionDone);

  const getTitle = (): string => {
    if (isRole.Student) {
      return t('lessonRelatedApplets.student.title');
    }

    if (isEarlyYears || isPrimaria) {
      return t('session.unblocked_applets.pe.title');
    }

    if (isJourneyClassroom) {
      return t('session.unblocked_applets.journeys.title');
    }

    return t('session.unblocked_applets.title');
  };

  const getDescription = (): JSX.Element => {
    if (isRole.Student) {
      return <></>;
    }

    const getDescriptionKey = (): string => {
      if (isEarlyYears || isPrimaria) {
        return 'session.unblocked_applets.pe.description';
      }
      
      if (isJourneyClassroom) {
        return 'session.unblocked_applets.journeys.description';
      }

      return 'session.unblocked_applets.description';
    };

    return (
      <Typography.Body2
        color={theme.tokens.color.alias.cm.text['text-subtle'].value}
      >
        <Trans
          i18nKey={getDescriptionKey()}
          components={{
            linktoblog: (
              <Typography.Link2
                href={INNOVAMAT_BLOG_URL}
                target="_blank"
                rel="noreferrer"
                color={theme.tokens.color.specific.element.link.value}
              />
            ),
          }}
        />
      </Typography.Body2>
    );
  };

  const handleClick = (appletId: string): void => {
    if (isInWebview) return;

    goToResource({
      type: 'applet',
      resourceId: appletId,
      openInNewTab: true,
    });
  };

  return (
    <StyledGridContainer>
      <GridRow>
        <StyledCol sm={7}>
          <UnblockedAppletsHeading>
            <UnblockedAppletsTitle>
              <Typography.H4>{getTitle()}</Typography.H4>
            </UnblockedAppletsTitle>
            {getDescription()}
          </UnblockedAppletsHeading>
        </StyledCol>
      </GridRow>

      <GridRow>
        <StyledCol>
          <ContentList
            academicYearId={user?.organization?.academicYearId!}
            classroomId={classroomId}
            list={appletsList}
            onResourceClick={(resource) => handleClick(resource.id)}
            onMarkSession={onMarkSession}
            onToggleSolutions={onToggleSolutions}
          />
        </StyledCol>
      </GridRow>
    </StyledGridContainer>
  );
}
